<template>
  <FormGroup>
    <!-- A building sets no move-in date: each unit becomes available on its own day -->
    <FormField v-if="!forBuilding" label="Available move-in date" :error="form.fieldErrors('desiredLeasingDate')">
      <Input v-model="form.fields.desiredLeasingDate" type="date" />
    </FormField>

    <FormGroup gap="medium">
      <Label>Desired leasing term</Label>
      <FormField v-for="item in possibleLeaseTerms" :key="item" :label="item" direction="row" label-position="after">
        <Checkbox :value="item" v-model="form.fields.possibleLeaseTerms" />
      </FormField>
      <FormFieldErrors :error="form.fieldErrors('possibleLeaseTerms')" />
    </FormGroup>
  </FormGroup>
</template>

<script setup lang="ts">
import { required } from '@vuelidate/validators';
import { useDateFormat } from '@vueuse/core';

import { Checkbox, FormField, FormFieldErrors, FormGroup, Input, Label, useForm } from '@tallo/design-system';
import { PossibleLeaseTerms } from '@tallo/property';

import { useDetailsHolder } from '../../composables/details-holder';
import { SaveLeaseConditionsDto } from '../../dtos/save-lease-condition.dto.interface';

const possibleLeaseTerms = Object.values(PossibleLeaseTerms);

const holder = useDetailsHolder();
/** A building's terms are what new units start with; the move-in date is each unit's own */
const forBuilding = holder.kind === 'building';
const current = holder.details?.leaseConditions;

const form = useForm<Pick<SaveLeaseConditionsDto, 'possibleLeaseTerms' | 'desiredLeasingDate'>>(
  {
    desiredLeasingDate: current?.desiredLeasingDate?.slice(0, 10) ?? useDateFormat(new Date(), 'YYYY-MM-DD').value,
    possibleLeaseTerms: current?.possibleLeaseTerms ?? [PossibleLeaseTerms.ONE_YEAR],
  },
  {
    desiredLeasingDate: forBuilding ? {} : { required },
    possibleLeaseTerms: { required },
  },
);

async function save() {
  return form.submit(async (fields) =>
    holder.saveLeaseConditions(
      forBuilding
        ? { possibleLeaseTerms: fields.possibleLeaseTerms }
        : {
            ...fields,
            desiredLeasingDate: fields.desiredLeasingDate ? new Date(fields.desiredLeasingDate).toISOString() : null,
          },
    ),
  );
}

defineExpose({ save, form });
</script>
